import React from 'react';
import { Impound } from '@/types';
import { Car, MapPin, Hash } from 'lucide-react';
import { resolveVehicleColorHex } from '@/utils/vehicleColor';

interface ImpoundCardProps {
  impound: Impound;
  onCardClick?: (impoundId: string) => void;
}

const ImpoundCard: React.FC<ImpoundCardProps> = ({ impound, onCardClick }) => {
  // Removed date formatting and days held calculation functions as they're no longer needed
  const vehicleColorHex = resolveVehicleColorHex(impound.vehicleColor);

  const formatVehicleDescription = (year: number, make: string, model: string) => {
    // If both make and model are not provided, show "Unspecified Vehicle"
    if ((!make || make == 'null' || make == 'Unknown') && (!model || model == 'null' || model == 'Unknown')) {
      return 'Unspecified Vehicle';
    }
    
    // Build the description parts
    const parts: string[] = [];
    
    // Add year only if it's valid (not 0, null, or blank)
    if (year && year > 0) {
      parts.push(year.toString());
    }
    
    // Add make if provided
    if (make && make !== 'null' && make !== 'unknown' && make !== 'Unknown') {
      parts.push(make);
    }
    
    // Add model if provided
    if (model && model !== 'null' && model !== 'unknown' && model !== 'Unknown') {
      parts.push(model);
    }
    
    return parts.join(' ');
  };

  return (
    <div 
      className="bg-background border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onCardClick?.(impound.id)}
    >
      {/* Header with Vehicle Info */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Car size={18} className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            {formatVehicleDescription(impound.vehicleYear, impound.vehicleMake, impound.vehicleModel)}
          </h3>
        </div>
        {impound.completedByCurrentDriver && (
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
            Completed by you
          </span>
        )}
      </div>

      {/* Vehicle Details Grid */}
      <div className="grid grid-cols-[35%_65%] gap-3 mb-3">
        <div className="flex items-center space-x-2">
          <Hash size={14} className="text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">License Plate</p>
            <p className="text-sm font-medium text-gray-900">
              {impound.licensePlate && impound.licensePlate.trim() !== '' ? impound.licensePlate : "N/A"}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Hash size={14} className="text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">VIN</p>
            <p className="text-sm font-medium text-gray-900">
              {impound.vin && impound.vin.trim() !== '' ? impound.vin : "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Impound Info */}
      <div className="grid grid-cols-[35%_65%] gap-3">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: vehicleColorHex }}></div>
          <div>
            <p className="text-xs text-gray-500">Color</p>
            <p className="text-sm font-medium text-gray-900">{impound.vehicleColor || 'Unknown'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <MapPin size={14} className="text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">Location</p>
            <p className="text-sm font-medium text-gray-900">{impound.lotLocation}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpoundCard; 